import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import { 
  Trophy, 
  Search,
  ChevronLeft,
  Plus,
  Trash2,
  Edit2,
  Loader2,
  Calendar as CalendarIcon,
  Play,
  Users
} from 'lucide-react';
import { formatCurrency, formatDate, cn } from '../lib/utils';

const getStatusBadge = (status) => {
  switch (status) {
    case 'registration': return { text: 'Anmeldung', className: 'bg-tunis-teal text-white' };
    case 'in_progress': return { text: 'Läuft', className: 'bg-amber-500 text-white' };
    case 'completed': return { text: 'Beendet', className: 'bg-slate-500 text-white' };
    default: return { text: status, className: 'bg-slate-500 text-white' };
  }
};

export default function AdminTournaments() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tournaments, setTournaments] = useState([]);
  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newTournament, setNewTournament] = useState({
    name: '',
    description: '',
    field_id: '',
    start_date: null,
    entry_fee: 50,
    prize_pool: 500,
    max_teams: 8,
    team_size: 5,
  });

  useEffect(() => {
    if (user?.role !== 'admin') {
      navigate('/dashboard');
      return;
    }

    fetchData();
  }, [user, navigate]);

  const fetchData = async () => {
    try {
      const [tournamentsRes, fieldsRes] = await Promise.all([
        api.get('/tournaments'),
        api.get('/admin/fields'),
      ]);
      setTournaments(tournamentsRes.data || []);
      setFields(fieldsRes.data || []);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newTournament.name || !newTournament.field_id || !newTournament.start_date) {
      toast.error('Name, Feld und Datum sind erforderlich');
      return;
    }

    setCreating(true);
    try {
      const params = new URLSearchParams({
        name: newTournament.name,
        description: newTournament.description || '',
        field_id: newTournament.field_id,
        start_date: format(newTournament.start_date, 'yyyy-MM-dd'),
        entry_fee: newTournament.entry_fee.toString(),
        prize_pool: newTournament.prize_pool.toString(),
        max_teams: newTournament.max_teams.toString(),
        team_size: newTournament.team_size.toString(),
      });

      await api.post(`/admin/tournaments?${params.toString()}`);
      toast.success('Turnier erstellt!');
      setCreateOpen(false);
      setNewTournament({
        name: '', description: '', field_id: '', start_date: null,
        entry_fee: 50, prize_pool: 500, max_teams: 8, team_size: 5,
      });
      await fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Erstellen');
    } finally {
      setCreating(false);
    }
  };

  const handleStart = async (tournamentId) => {
    try {
      await api.post(`/tournaments/${tournamentId}/start`);
      toast.success('Turnier gestartet!');
      await fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Starten');
    }
  };

  const handleDelete = async (tournamentId, tournamentName) => {
    if (!window.confirm(`Turnier "${tournamentName}" wirklich löschen?`)) return;

    try {
      await api.delete(`/admin/tournaments/${tournamentId}`);
      toast.success('Turnier gelöscht');
      await fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Löschen');
    }
  };

  const filteredTournaments = tournaments.filter(t =>
    t.name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <Layout>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Skeleton className="h-8 w-64 mb-8" />
          <Skeleton className="h-96 rounded-2xl" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="admin-tournaments">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Button variant="ghost" size="icon" onClick={() => navigate('/admin')}>
            <ChevronLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1">
            <h1 className="font-display font-bold text-2xl text-tunis-blue">
              Turniere verwalten
            </h1>
            <p className="text-muted-foreground">{tournaments.length} Turniere</p>
          </div>
          <Dialog open={createOpen} onOpenChange={setCreateOpen}>
            <DialogTrigger asChild>
              <Button className="btn-primary">
                <Plus className="w-5 h-5 mr-2" />
                Neues Turnier
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>Neues Turnier erstellen</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreate} className="space-y-4 pt-4">
                <div className="space-y-2">
                  <Label>Name *</Label>
                  <Input
                    value={newTournament.name}
                    onChange={(e) => setNewTournament(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="z.B. Summer Cup 2025"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Beschreibung</Label>
                  <Textarea
                    value={newTournament.description}
                    onChange={(e) => setNewTournament(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Turnierbeschreibung..."
                    rows={2}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Spielfeld *</Label>
                  <Select
                    value={newTournament.field_id}
                    onValueChange={(value) => setNewTournament(prev => ({ ...prev, field_id: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Feld auswählen" />
                    </SelectTrigger>
                    <SelectContent>
                      {fields.map(field => (
                        <SelectItem key={field.id} value={field.id}>
                          {field.name} - {field.location}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Startdatum *</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn("w-full justify-start", !newTournament.start_date && "text-muted-foreground")}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {newTournament.start_date ? format(newTournament.start_date, 'PPP', { locale: de }) : 'Datum wählen'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <Calendar
                        mode="single"
                        selected={newTournament.start_date}
                        onSelect={(date) => setNewTournament(prev => ({ ...prev, start_date: date }))}
                        disabled={(date) => date < new Date()}
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Eintritt (TND)</Label>
                    <Input
                      type="number"
                      value={newTournament.entry_fee}
                      onChange={(e) => setNewTournament(prev => ({ ...prev, entry_fee: parseFloat(e.target.value) }))}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Preisgeld (TND)</Label>
                    <Input
                      type="number"
                      value={newTournament.prize_pool}
                      onChange={(e) => setNewTournament(prev => ({ ...prev, prize_pool: parseFloat(e.target.value) }))}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Max. Teams</Label>
                    <Select
                      value={newTournament.max_teams.toString()}
                      onValueChange={(value) => setNewTournament(prev => ({ ...prev, max_teams: parseInt(value) }))}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {[4, 8, 16, 32].map(n => (
                          <SelectItem key={n} value={n.toString()}>{n} Teams</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Teamgröße</Label>
                    <Select
                      value={newTournament.team_size.toString()}
                      onValueChange={(value) => setNewTournament(prev => ({ ...prev, team_size: parseInt(value) }))}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {[3, 4, 5, 6, 7, 11].map(n => (
                          <SelectItem key={n} value={n.toString()}>{n} Spieler</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <Button type="submit" className="w-full btn-primary" disabled={creating}>
                  {creating ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Turnier erstellen'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Search */}
        <div className="relative mb-6">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
          <Input
            placeholder="Turnier suchen..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 h-12"
          />
        </div>

        {/* Tournament Cards */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredTournaments.map((tournament) => {
            const statusBadge = getStatusBadge(tournament.status);
            return (
              <Card key={tournament.id} className="border-slate-200">
                <CardContent className="p-5">
                  <div className="flex justify-between items-start mb-3">
                    <Badge className={statusBadge.className}>{statusBadge.text}</Badge>
                    <div className="flex gap-1">
                      {tournament.status === 'registration' && tournament.team_count >= 2 && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-green-600 hover:bg-green-50"
                          onClick={() => handleStart(tournament.id)}
                        >
                          <Play className="w-4 h-4" />
                        </Button>
                      )}
                      {tournament.status !== 'in_progress' && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-red-600 hover:bg-red-50"
                          onClick={() => handleDelete(tournament.id, tournament.name)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                  <h3 className="font-bold text-lg text-tunis-blue mb-2">{tournament.name}</h3>
                  <div className="space-y-1 text-sm text-muted-foreground mb-4">
                    <div className="flex items-center gap-2">
                      <CalendarIcon className="w-4 h-4" />
                      {formatDate(tournament.start_date)}
                    </div>
                    <div className="flex items-center gap-2">
                      <Users className="w-4 h-4" />
                      {tournament.team_count}/{tournament.max_teams} Teams
                    </div>
                  </div>
                  <div className="flex justify-between items-center pt-3 border-t border-slate-100">
                    <div className="text-sm">
                      Eintritt: <span className="font-bold">{formatCurrency(tournament.entry_fee)}</span>
                    </div>
                    <div className="text-sm">
                      Preis: <span className="font-bold text-amber-600">{formatCurrency(tournament.prize_pool)}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {filteredTournaments.length === 0 && (
          <Card className="border-slate-200">
            <CardContent className="py-12 text-center">
              <Trophy className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <p className="text-muted-foreground">Keine Turniere gefunden</p>
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
}
